import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogType?: "website" | "article" | "profile";
  ogImage?: string;
  twitterCard?: "summary" | "summary_large_image";
  noIndex?: boolean;
  structuredData?: object | object[];
  children?: React.ReactNode;
}

const DEFAULT_TITLE = "TPS Housing — Temporary Housing Solutions";
const DEFAULT_DESCRIPTION = "Professional temporary housing solutions for insurance claims and displaced families. Expert placement services for adjusters and policyholders.";
const BASE_URL = "https://tpshousing.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  twitterCard = "summary_large_image",
  noIndex = false,
  structuredData,
  children,
}: SEOProps) => {
  const fullTitle = title ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  const fullCanonicalUrl = canonicalUrl ? `${BASE_URL}${canonicalUrl}` : undefined;

  // Handle both single object and array of structured data
  const structuredDataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={DEFAULT_TITLE} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) */}
      {structuredDataArray.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}

      {children}
    </Helmet>
  );
};

// Pre-defined structured data schemas
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "TPS Housing",
  "description": "Professional temporary housing solutions for insurance claims and displaced families",
  "url": "https://tpshousing.com",
  "logo": "https://tpshousing.com/favicon.ico",
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["English", "Spanish"]
  }
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "TPS Housing",
  "url": "https://tpshousing.com",
  "description": "Temporary housing placement services for insurance adjusters and policyholders",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://tpshousing.com/property-search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "TPS Housing",
  "@id": "https://tpshousing.com",
  "url": "https://tpshousing.com",
  "description": "Expert temporary housing placement services for families displaced by insurance claims",
  "priceRange": "$$",
  "telephone": "+1-760-687-1001",
  "image": "https://tpshousing.com/og-image.jpg",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "10810 N. Tatum Blvd. #102-222",
    "addressLocality": "Phoenix",
    "addressRegion": "AZ",
    "postalCode": "85028",
    "addressCountry": "US"
  },
  "areaServed": "US",
  "serviceType": "Temporary Housing Placement"
};
